import ArrowDownIcon from '@/storybook/icons/ArrowDownIcon';
import ArrowUpAndDownIcon from '@/storybook/icons/ArrowUpAndDownIcon';
import ArrowUpIcon from '@/storybook/icons/ArrowUpIcon';
import { formatNumberWithSpaces, formatToPercentage } from '@/utils';
import { ReactNode, useMemo } from 'react';
import styles from './ScoreArrow.module.css';

export type ScoreArrowProps = {
  'value': number | null | undefined;
  'formatValue'?: boolean;
  'isDisplayParenthesis'?: boolean;
  'data-testid'?: string;
  'arrowSuffix'?: ReactNode;
  'fractionDigits'?: number;
  'formatValueWithSeparators'?: boolean;
  'separator'?: string;
};

export const ARROW_COLORS = ['GREEN', 'RED', 'GREY'] as const;

/** `Number.isFinite` does not narrow, so the nullish case needs an explicit guard. */
const isRenderableNumber = (value: number | null | undefined): value is number =>
  typeof value === 'number' && Number.isFinite(value);

export default function ScoreArrow({
  value,
  formatValue,
  'data-testid': dataTestId = 'scoreArrow',
  isDisplayParenthesis,
  arrowSuffix = '%',
  fractionDigits = 1,
  formatValueWithSeparators = false,
  separator = ' ',
}: ScoreArrowProps) {
  const formatedArrowValue = useMemo(() => {
    if (!isRenderableNumber(value)) return 'N/A';
    if (formatValueWithSeparators) {
      const formattedValue = formatNumberWithSpaces(Math.abs(value), separator);
      return `${formattedValue}${arrowSuffix}`;
    }
    const formatedPercentage = formatToPercentage(value, 2);
    const newFormatedValue = formatValue ? parseFloat(formatedPercentage) : value;
    const newFormatedArrowValue = Math.abs(newFormatedValue).toFixed(fractionDigits);
    return `${newFormatedArrowValue}${arrowSuffix}`;
  }, [formatValue, value, arrowSuffix, fractionDigits, formatValueWithSeparators, separator]);

  const ArrowIcon = useMemo(() => {
    if (!isRenderableNumber(value) || value === 0) return ArrowUpAndDownIcon;
    return value < 0 ? ArrowDownIcon : ArrowUpIcon;
  }, [value]);

  const ArrowIconColor: (typeof ARROW_COLORS)[number] = useMemo(() => {
    if (ArrowIcon === ArrowUpIcon) return 'GREEN';
    if (ArrowIcon === ArrowDownIcon) return 'RED';
    return 'GREY';
  }, [ArrowIcon]);

  return (
    <div
      className={styles.arrowValueContainer}
      data-color={ArrowIconColor}
      data-with-parenthesis={isDisplayParenthesis}
      data-testid={dataTestId}
    >
      <ArrowIcon />
      <span>{formatedArrowValue}</span>
    </div>
  );
}
